import { authedFetch } from './authed-fetch';

const PREFIX = 'employee-share-thumbnails-v1:';
const MAX_BYTES = 32 * 1024 * 1024;
const MAX_IMAGE_BYTES = 2 * 1024 * 1024;
// Serialize cache mutations, including logout, so late writes cannot undo cleanup.
let work: Promise<unknown> = Promise.resolve();
let warned = false;
function storage<T>(fn: () => Promise<T>): Promise<T | undefined> {
  const result = work.then(fn).catch(error => {
    if (!warned) {
      warned = true;
      console.warn('[thumbnails] Persistent cache unavailable; using memory only.', error);
    }
    return undefined;
  });
  work = result;
  return result;
}

export function cleanThumbnailStorage(scope: string | null, clearCurrent = false) {
  return storage(async () => {
    if (!globalThis.caches) return;
    for (const name of await caches.keys()) {
      if (name.startsWith(PREFIX) && (clearCurrent || name !== PREFIX + scope)) await caches.delete(name);
    }
  });
}

export function removeThumbnailBytes(scope: string, shareId: number) {
  return storage(async () => {
    if (!globalThis.caches) return;
    const cache = await caches.open(PREFIX + scope);
    const prefix = `/api/activity-bar/preview-thumbnails/${shareId}/`;
    for (const request of await cache.keys()) {
      if (new URL(request.url).pathname.startsWith(prefix)) await cache.delete(request);
    }
  });
}

export async function loadThumbnailBytes(
  scope: string, contentUrl: string, valid: () => boolean, signal: AbortSignal,
): Promise<Blob | null> {
  // Only server-issued, same-origin versioned routes; never fetch arbitrary URLs.
  if (!/^\/api\/activity-bar\/preview-thumbnails\/[1-9]\d*\/[a-f0-9]{64}$/.test(contentUrl)) {
    throw new Error('Invalid thumbnail content URL');
  }
  const name = PREFIX + scope;
  const key = new URL(contentUrl, window.location.origin).href;
  const cached = await storage(async () => {
    if (!globalThis.caches || !valid()) return;
    const cache = await caches.open(name);
    const response = await cache.match(key);
    if (!response) return;
    const blob = await response.blob();
    if (!blob.size || blob.size > MAX_IMAGE_BYTES || blob.type !== 'image/webp') {
      await cache.delete(key);
      return;
    }
    // Cache insertion order is our disk LRU.
    await cache.delete(key);
    await cache.put(key, new Response(blob, { headers: { 'Content-Type': blob.type, 'Content-Length': String(blob.size) } }));
    return blob;
  });
  if (!valid()) return null;
  if (cached) return cached;
  const response = await authedFetch(contentUrl, { signal, cache: 'no-store' });
  if (!response.ok) throw new Error(`Thumbnail could not be loaded (${response.status})`);
  const blob = await response.blob();
  if (!blob.size || blob.size > MAX_IMAGE_BYTES || blob.type !== 'image/webp') throw new Error('Invalid thumbnail image');
  if (!valid()) return null;
  await storage(async () => {
    if (!globalThis.caches || !valid()) return;
    const cache = await caches.open(name);
    await cache.put(key, new Response(blob, { headers: { 'Content-Type': blob.type, 'Content-Length': String(blob.size) } }));
    const keys = await cache.keys();
    let total = 0;
    let count = 0;
    for (const request of [...keys].reverse()) {
      const item = await cache.match(request);
      total += Number(item?.headers.get('Content-Length') || MAX_IMAGE_BYTES);
      if (++count > 200 || total > MAX_BYTES) await cache.delete(request);
    }
  });
  return valid() ? blob : null;
}